/**
 * 当前用户信息 Hook
 *
 * @description 获取和管理当前登录用户的信息
 */

import { useState, useEffect } from 'react'
import { getCurrentUser, type User } from '@/api/user'

interface UseCurrentUserResult {
  /** 当前用户信息 */
  user: User | null
  /** 是否正在加载 */
  isLoading: boolean
  /** 错误信息 */
  error: Error | null
  /** 刷新用户信息 */
  refresh: () => Promise<void>
}

/**
 * 获取当前用户信息的 Hook
 * @returns 用户信息、加载状态和刷新函数
 */
export const useCurrentUser = (): UseCurrentUserResult => {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)

  const fetchUser = async () => {
    try {
      setIsLoading(true)
      setError(null)
      const userData = await getCurrentUser()
      setUser(userData)
    } catch (err) {
      const error = err instanceof Error ? err : new Error('获取用户信息失败')
      setError(error)
      console.error('获取用户信息失败:', error)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchUser()
  }, [])

  return {
    user,
    isLoading,
    error,
    refresh: fetchUser
  }
}
